<template>
  <div
    class="pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg max-w-md"
    :class="toastClasses"
    role="alert"
  >
    <!-- Icon -->
    <div class="flex-shrink-0">
      <svg
        v-if="toast.type === 'success'"
        class="w-5 h-5"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
          clip-rule="evenodd"
        />
      </svg>
      <svg
        v-else-if="toast.type === 'error'"
        class="w-5 h-5"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fill-rule="evenodd"
          d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
          clip-rule="evenodd"
        />
      </svg>
      <svg
        v-else-if="toast.type === 'warning'"
        class="w-5 h-5"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fill-rule="evenodd"
          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
          clip-rule="evenodd"
        />
      </svg>
      <svg v-else class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
        <path
          fill-rule="evenodd"
          d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
          clip-rule="evenodd"
        />
      </svg>
    </div>

    <!-- Message -->
    <div class="flex-1 text-sm font-medium">
      {{ toast.message }}
    </div>

    <!-- Close button -->
    <button
      @click="$emit('close')"
      class="flex-shrink-0 -mr-1 p-1 rounded-md opacity-60 hover:opacity-100 transition-opacity"
      :class="closeButtonClasses"
      aria-label="关闭"
    >
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
        <path
          fill-rule="evenodd"
          d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
          clip-rule="evenodd"
        />
      </svg>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { Toast } from "@/composables/useNotification";

interface Props {
  toast: Toast;
}

const props = defineProps<Props>();

defineEmits<{
  (e: "close"): void;
}>();

const toastClasses = computed(() => {
  const base = "border bg-white dark:bg-gray-800 text-gray-900 dark:text-white";
  switch (props.toast.type) {
    case "success":
      return `${base} border-green-200 dark:border-green-700`;
    case "error":
      return `${base} border-red-200 dark:border-red-700`;
    case "warning":
      return `${base} border-yellow-200 dark:border-yellow-700`;
    case "info":
    default:
      return `${base} border-blue-200 dark:border-blue-700`;
  }
});

const closeButtonClasses = computed(() => {
  switch (props.toast.type) {
    case "success":
      return "text-green-700 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/30";
    case "error":
      return "text-red-700 dark:text-red-300 hover:bg-red-100 dark:hover:bg-red-900/30";
    case "warning":
      return "text-yellow-700 dark:text-yellow-300 hover:bg-yellow-100 dark:hover:bg-yellow-900/30";
    case "info":
    default:
      return "text-blue-700 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/30";
  }
});
</script>
